import { MOTION } from '@/components/common/motion';
import { SelectionCheckbox } from '@/components/common/SelectionCheckbox';
import { CHIP } from '@/components/common/surfaces';
import { ModelPreview } from '@/components/project-detail/ModelPreview';
import { CollisionStatusTag } from '@/components/projects/CollisionStatusTag';
import type { ProjectListItem } from '@/types/project.types';
import type { RuleSetListItem } from '@/types/ruleSet.types';
import { extractModelIdFromUrl, formatDateTime, formatUserName } from '@/utils/formatters';
import { projectRuleSetInfo } from '@/utils/ruleSets';
import { CalendarBlank, ListChecks, User, Warning } from '@phosphor-icons/react';
import { Tooltip } from 'primereact/tooltip';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface ProjectCardProps {
  project: ProjectListItem;
  onClick: (projectId: string) => void;
  onUserClick?: (ownerId: string) => void;
  isSelected?: boolean;
  onSelect?: (projectId: string, selected: boolean) => void;
  /** Every rule set the backend offers, to show the project's set by title. */
  ruleSets?: RuleSetListItem[];
}

/**
 * One project in the grid.
 *
 * The card is 4 px of padding around a preview, so the preview takes the 12 px radius that sits
 * one step inside the card's 16 px. Everything below it — title, dates, tags — shares one 12 px
 * gutter, which together with that 4 px lines the text up with the preview's edge.
 */
export const ProjectCard: FC<ProjectCardProps> = ({
  project,
  onClick,
  onUserClick,
  isSelected = false,
  onSelect,
  ruleSets = [],
}) => {
  const { t } = useTranslation();
  // Both URLs carry the same step-document id, so either one resolves the preview.
  const previewUrl = project.model_png_url || project.stock_png_url;
  const modelId = extractModelIdFromUrl(previewUrl);
  const userName = formatUserName(project);
  const ruleSet = projectRuleSetInfo(project, ruleSets);

  const handleUserClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering project click
    if (onUserClick) {
      onUserClick(project.owner_id);
    }
  };

  const hintClass = `project-apt-error-hint-${project.id}`;
  const errorHint = project.apt_generation_error ?? t('home.projectHasErrors');

  return (
    <div
      className={`flex flex-col rounded-2xl border bg-white p-1 ${MOTION} cursor-pointer hover:-translate-y-0.5 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 ${
        isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-gray-200 hover:border-gray-300'
      }`}
      onClick={() => onClick(project.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(project.id);
        }
      }}
      tabIndex={0}
      role="button"
    >
      {/* Preview: 12 px radius, one step inside the card's 16 px at 4 px of padding. */}
      <div className="relative aspect-video overflow-hidden rounded-xl bg-gray-50">
        <ModelPreview modelId={modelId} alt={`Preview of ${project.title}`} />

        {project.has_errors && (
          <>
            <Tooltip target={`.${hintClass}`} position="bottom" style={{ maxWidth: '24rem' }} />
            <span
              className={`${hintClass} absolute left-2 top-2 z-10 flex h-8 w-8 cursor-help items-center justify-center rounded-full bg-amber-100 text-amber-600`}
              data-pr-tooltip={errorHint}
              aria-label={errorHint}
            >
              <Warning size={16} aria-hidden="true" />
            </span>
          </>
        )}

        {onSelect && (
          <span className="absolute right-2 top-2 z-10">
            <SelectionCheckbox
              checked={isSelected}
              onChange={(selected) => onSelect(project.id, selected)}
              aria-label={t('home.selectProject')}
            />
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2 px-3 pb-3 pt-3">
        <h3 className="m-0 truncate text-base font-semibold text-gray-900" title={project.title}>
          {project.title}
        </h3>

        <div className="flex items-center gap-2 text-sm text-gray-500">
          <CalendarBlank size={16} className="shrink-0" aria-hidden="true" />
          <span className="truncate">{formatDateTime(project.created_at)}</span>
        </div>

        {onUserClick ? (
          <button
            type="button"
            onClick={handleUserClick}
            className={`flex w-fit max-w-full items-center gap-2 rounded text-sm text-gray-500 underline-offset-2 hover:text-gray-900 hover:underline active:scale-[0.98] ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2`}
            title={userName}
          >
            <User size={16} className="shrink-0" aria-hidden="true" />
            <span className="truncate">{userName}</span>
          </button>
        ) : (
          <span className="flex items-center gap-2 text-sm text-gray-500" title={userName}>
            <User size={16} className="shrink-0" aria-hidden="true" />
            <span className="truncate">{userName}</span>
          </span>
        )}

        {ruleSet && (
          <div className="flex items-center gap-2 text-sm text-gray-500" title={t('ruleSets.title')}>
            <ListChecks size={16} className="shrink-0" aria-hidden="true" />
            <span className="truncate">{ruleSet.label}</span>
            {ruleSet.version !== null && (
              <span className="shrink-0 text-xs text-gray-400">
                {t('ruleSets.versionLabel', { version: ruleSet.version })}
              </span>
            )}
            {ruleSet.changed && (
              <span
                className="shrink-0 text-amber-600"
                title={t('ruleSets.modifiedSince', { version: ruleSet.currentVersion })}
              >
                <Warning size={16} aria-hidden="true" />
              </span>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-2">
            {project.part_type && <span className={CHIP}>{project.part_type}</span>}
            {project.model_png_url && <span className={CHIP}>{t('home.badgeModel')}</span>}
            {project.stock_png_url && <span className={CHIP}>{t('home.badgeStock')}</span>}
          </div>
          <CollisionStatusTag state={project.collision_simulation_state} />
        </div>
      </div>
    </div>
  );
};
